import React, { useState, useEffect } from 'react';

/**
 * SendOffertModal - Bekräftelsedialog vid skickning av offert
 *
 * Om version > 1.0: visar en valfri textarea för ändringssammanfattning
 * Om version === 1.0: bara bekräftelse
 *
 * Om offertMailSent === false (första offerten för upphandlingen): visar ett
 * förifyllt, redigerbart meddelande som skickas med mailet och visas för kunden
 * ovanför "Mina Offerter". Efterföljande offerter mailar inte (inget fält visas).
 *
 * defaultChangeSummary: Förval från PUThread.changeSummary (fylls i av BK vid PU-skickning)
 */
const buildDefaultMessage = (firstName, bkName) => {
  const hej = firstName ? `Hej ${firstName}!` : 'Hej!';
  const sign = bkName ? `\n\nVänliga hälsningar,\n${bkName}` : '';
  return `${hej}\n\nHär kommer er offert för projektet. Titta gärna extra noga på upplägget och hör av er om något är oklart.${sign}`;
};

const SendOffertModal = ({ isOpen, onClose, onConfirm, version, sending, defaultChangeSummary, offertMailSent = false, customerFirstName = '', bkFirstName = '', offertId = null, skickarInloggningsuppgifter = false }) => {
  const [changeSummary, setChangeSummary] = useState(defaultChangeSummary || '');
  const [customerMessage, setCustomerMessage] = useState('');
  const [loadingMessage, setLoadingMessage] = useState(false);

  useEffect(() => {
    if (defaultChangeSummary) {
      setChangeSummary(defaultChangeSummary);
    }
  }, [defaultChangeSummary]);

  // Förifyll meddelandet när modalen öppnas (bara om mailet inte redan skickats).
  // Hämtar en mall från backend där Inredningsarkitekt-punkterna speglar kalkylens
  // IA-baspaket (samma logik som Brief). Faller tillbaka till en enkel text vid fel.
  useEffect(() => {
    if (!isOpen || offertMailSent) return;

    let cancelled = false;
    const fallback = () => buildDefaultMessage(customerFirstName, bkFirstName);

    if (!offertId) {
      setCustomerMessage(fallback());
      return;
    }

    setLoadingMessage(true);
    const token = localStorage.getItem('token');
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/offert/${offertId}/intro-message`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : Promise.reject(new Error('intro-message misslyckades')))
      .then(data => {
        if (cancelled) return;
        setCustomerMessage(data?.message || fallback());
      })
      .catch(() => {
        if (!cancelled) setCustomerMessage(fallback());
      })
      .finally(() => {
        if (!cancelled) setLoadingMessage(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, offertMailSent, customerFirstName, bkFirstName, offertId]);

  const isUpdatedVersion = version && parseFloat(version) > 1.0;
  const showMessageField = !offertMailSent;

  const handleConfirm = () => {
    onConfirm(
      isUpdatedVersion ? changeSummary : '',
      showMessageField ? customerMessage : ''
    );
    setChangeSummary('');
    setCustomerMessage('');
  };

  const handleClose = () => {
    setChangeSummary('');
    setCustomerMessage('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[1100]"
      role="dialog"
      aria-modal="true"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Skicka offert{isUpdatedVersion ? ` (version ${version})` : ''}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {isUpdatedVersion
              ? 'Beskriv kort vad som ändrats sedan förra versionen — detta visas för kunden i portalen och i mailet.'
              : 'Offerten skickas till kunden via e-post med en länk för att granska och svara.'}
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto space-y-4">
          {/* Upphandlingar som hoppat över estimatsteget — kunden har ett konto
              sedan bokningen men har aldrig fått veta om portalen. Uppgifterna
              följer med det här mailet, och bara det här. */}
          {skickarInloggningsuppgifter && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2.5">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              <p className="text-sm text-blue-900">
                <span className="font-medium">Kunden får sina inloggningsuppgifter i det här mailet.</span>{' '}
                Projektet startades utan estimat, så varken beställare 1 eller 2 har fått
                tillgång till portalen tidigare. Uppgifterna skickas bara en gång — en ny
                offertversion återställer inte lösenordet.
              </p>
            </div>
          )}

          {isUpdatedVersion && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ändringskommentar <span className="text-gray-400 font-normal">(valfritt)</span>
              </label>
              <textarea
                value={changeSummary}
                onChange={(e) => setChangeSummary(e.target.value)}
                placeholder="T.ex. Uppdaterat pris för snickeri, lagt till extra rum…"
                className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-leadme-pink focus:border-transparent resize-none"
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-2">
                Texten visas för kunden så de förstår vad som ändrats sedan förra versionen.
              </p>
            </div>
          )}

          {showMessageField ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meddelande till kund <span className="text-gray-400 font-normal">(visas i portalen)</span>
                {loadingMessage && <span className="text-gray-400 font-normal ml-2">Hämtar mall…</span>}
              </label>
              <textarea
                value={customerMessage}
                onChange={(e) => setCustomerMessage(e.target.value)}
                placeholder={loadingMessage ? 'Hämtar förifyllt meddelande…' : 'Skriv ett meddelande till kunden…'}
                className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-leadme-pink focus:border-transparent resize-none"
                rows={12}
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-2">
                Meddelandet visas för kunden i portalen ovanför "Mina Offerter" (skickas inte i mailet). Kunden får ett separat standardmail om att en offert finns. Lämna tomt för inget meddelande.
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-700">
              {isUpdatedVersion
                ? 'När du klickar "Skicka offert" får kunden ett mail om att en uppdaterad offert finns att granska (med ev. ändringskommentar) och kan svara i portalen.'
                : 'När du klickar "Skicka offert" notifieras kunden via e-post och kan därefter granska och svara på offerten.'}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={sending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Avbryt
          </button>
          <button
            onClick={handleConfirm}
            disabled={sending}
            className="px-4 py-2 text-sm font-medium text-white bg-leadme-pink rounded-lg hover:bg-leadme-pink-dark disabled:opacity-50 flex items-center gap-2"
          >
            {sending && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            <span>{sending ? 'Skickar…' : 'Skicka offert'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendOffertModal;
